import { useSyncExternalStore } from 'react';

import { applyBrandingVars, brandingStore } from './branding.js';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'aurora.theme';
const listeners = new Set<() => void>();

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

let current: Theme = (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? systemTheme();

/** Wählbare Akzent-Presets (Personalisierung pro Gerät/Nutzer). */
export const ACCENTS = [
  { key: 'gold', label: 'Gold' },
  { key: 'blue', label: 'Blau' },
  { key: 'green', label: 'Grün' },
  { key: 'purple', label: 'Violett' },
  { key: 'rose', label: 'Rosé' },
  { key: 'teal', label: 'Türkis' },
] as const;
/** 'brand' = die Markenfarbe des Mandanten (v6/E1) — Default, wenn Branding gepflegt ist. */
export type Accent = (typeof ACCENTS)[number]['key'] | 'brand';

const ACCENT_KEY = 'aurora.accent';
let storedAccent: Accent | null = localStorage.getItem(ACCENT_KEY) as Accent | null;

/**
 * Ohne gespeicherte Nutzerwahl gilt: Marke, falls der Mandant eine
 * Markenfarbe gepflegt hat, sonst Gold. Hat der Nutzer „Marke" gewählt,
 * das Branding aber keine Farbe (mehr), fällt die Anzeige auf Gold zurück.
 */
function resolveAccent(): Accent {
  const hasBrandColor = brandingStore.get()?.accentHex != null;
  const accent = storedAccent ?? (hasBrandColor ? 'brand' : 'gold');
  return accent === 'brand' && !hasBrandColor ? 'gold' : accent;
}

/** v6-Design: Listendichte — „komfortabel" (Default) oder „kompakt". */
export type Density = 'comfortable' | 'compact';
const DENSITY_KEY = 'aurora.density';
let currentDensity: Density =
  (localStorage.getItem(DENSITY_KEY) as Density | null) ?? 'comfortable';

export function applyTheme(): void {
  const accent = resolveAccent();
  document.documentElement.dataset['theme'] = current;
  document.documentElement.dataset['accent'] = accent;
  document.documentElement.dataset['density'] = currentDensity;
  // Branding zuletzt: Inline-Variablen (Marke/Grundton) schlagen die
  // data-accent-Presets — bei „Marke" die volle abgeleitete Skala.
  applyBrandingVars(current, accent === 'brand');
}

export const densityStore = {
  get(this: void): Density {
    return currentDensity;
  },
  toggle(this: void): void {
    currentDensity = currentDensity === 'compact' ? 'comfortable' : 'compact';
    localStorage.setItem(DENSITY_KEY, currentDensity);
    applyTheme();
    for (const listener of listeners) listener();
  },
  subscribe(this: void, listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useDensity(): Density {
  return useSyncExternalStore(densityStore.subscribe, densityStore.get);
}

export const accentStore = {
  get(this: void): Accent {
    return resolveAccent();
  },
  set(this: void, accent: Accent): void {
    storedAccent = accent;
    localStorage.setItem(ACCENT_KEY, accent);
    applyTheme();
    for (const listener of listeners) listener();
  },
  subscribe(this: void, listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useAccent(): Accent {
  return useSyncExternalStore(accentStore.subscribe, accentStore.get);
}

export const themeStore = {
  get(this: void): Theme {
    return current;
  },
  toggle(this: void): void {
    current = current === 'dark' ? 'light' : 'dark';
    localStorage.setItem(STORAGE_KEY, current);
    applyTheme();
    for (const listener of listeners) listener();
  },
  subscribe(this: void, listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useTheme(): Theme {
  return useSyncExternalStore(themeStore.subscribe, themeStore.get);
}

// Branding-Änderungen (Fetch fertig, PUT gespeichert) wirken sofort:
// Variablen neu anwenden und Theme-Abonnenten (useAccent etc.) wecken.
brandingStore.subscribe(() => {
  applyTheme();
  for (const listener of listeners) listener();
});
